import React, { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '../services/api'
import Button from '../components/Button'
import Input from '../components/Input'
import LocaleToggle from '../components/LocaleToggle'
import CategoryTranslationBanner from '../components/CategoryTranslationBanner'
import { useLanguageStore } from '../stores/languageStore'
import { Edit, Trash2 } from 'lucide-react'

const Categories = () => {
  const [isCreating, setIsCreating] = useState(false)
  const [editingCategory, setEditingCategory] = useState(null)
  const [showOtherLocales, setShowOtherLocales] = useState(false)
  const [formData, setFormData] = useState({
    slug: '',
    type: 'general',
    icon: '',
    color: '',
    is_active: true,
    sort_order: 0,
    parent_id: null,
    translations: {},
  })

  const queryClient = useQueryClient()
  const { currentLanguageCode } = useLanguageStore()

  const { data: categories = [], isLoading } = useQuery({
    queryKey: ['categories', currentLanguageCode, showOtherLocales],
    queryFn: async () => {
      const response = await api.get('/categories', {
        params: {
          locale: currentLanguageCode || 'en',
          include_other_locales: showOtherLocales,
        },
      })
      return response.data.data
    },
    placeholderData: (previousData) => previousData,
  })

  const { data: languagesRes } = useQuery({
    queryKey: ['languages'],
    queryFn: async () => {
      const response = await api.get('/languages')
      return response.data
    },
  })

  const languages = Array.isArray(languagesRes?.data)
    ? languagesRes.data.filter((lang) => lang.is_active)
    : Array.isArray(languagesRes)
    ? languagesRes.filter((lang) => lang.is_active)
    : []

  const currentLanguage = languages.find(
    (lang) => lang.code === currentLanguageCode
  )
  const currentLanguageName = currentLanguage?.name || 'English'
  const hasTranslation = editingCategory
    ? editingCategory.has_translation_for_locale ?? true
    : true

  const createMutation = useMutation({
    mutationFn: (data) => api.post('/categories', data),
    onSuccess: () => {
      queryClient.invalidateQueries(['categories'])
      setIsCreating(false)
      resetForm()
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => api.put(`/categories/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['categories'])
      setEditingCategory(null)
      resetForm()
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/categories/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries(['categories'])
    },
  })

  const resetForm = () => {
    setFormData({
      slug: '',
      type: 'general',
      icon: '',
      color: '',
      is_active: true,
      sort_order: 0,
      parent_id: null,
      translations: {},
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    const data = {
      ...formData,
      translations: Object.entries(formData.translations).map(
        ([locale, translation]) => ({
          locale,
          ...translation,
        })
      ),
    }

    if (editingCategory) {
      updateMutation.mutate({ id: editingCategory.id, data })
    } else {
      createMutation.mutate(data)
    }
  }

  const handleEdit = (category) => {
    setEditingCategory(category)
    setFormData({
      slug: category.slug,
      type: category.type,
      icon: category.icon || '',
      color: category.color || '',
      is_active: category.is_active,
      sort_order: category.sort_order,
      parent_id: category.parent_id,
      translations: category.translations.reduce((acc, translation) => {
        acc[translation.locale] = {
          name: translation.name,
          description: translation.description || '',
          meta_description: translation.meta_description || '',
        }
        return acc
      }, {}),
    })
    setIsCreating(true)
  }

  const handleDelete = (id) => {
    if (confirm('Are you sure you want to delete this category?')) {
      deleteMutation.mutate(id)
    }
  }

  const updateTranslation = (locale, field, value) => {
    setFormData((prev) => ({
      ...prev,
      translations: {
        ...prev.translations,
        [locale]: {
          ...prev.translations[locale],
          [field]: value,
        },
      },
    }))
  }

  if (isLoading) return <div>Loading...</div>

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Categories</h1>
        <div className="flex gap-3">
          <LocaleToggle
            enabled={showOtherLocales}
            onChange={setShowOtherLocales}
          />
          <Button onClick={() => setIsCreating(true)}>Add Category</Button>
        </div>
      </div>

      {isCreating && (
        <>
          {editingCategory && !hasTranslation && (
            <CategoryTranslationBanner
              category={editingCategory}
              locale={currentLanguageCode}
              languageName={currentLanguageName}
              onTranslationCreated={() => {
                queryClient.invalidateQueries([
                  'categories',
                  currentLanguageCode,
                ])
                setIsCreating(false)
                setEditingCategory(null)
              }}
            />
          )}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-lg font-semibold mb-4">
              {editingCategory ? 'Edit Category' : 'Add New Category'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Slug"
                  value={formData.slug}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, slug: e.target.value }))
                  }
                  required
                />
                <Input
                  label="Type"
                  value={formData.type}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, type: e.target.value }))
                  }
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Icon"
                  value={formData.icon}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, icon: e.target.value }))
                  }
                />
                <Input
                  label="Color"
                  type="color"
                  value={formData.color}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, color: e.target.value }))
                  }
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Sort Order"
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      sort_order: parseInt(e.target.value),
                    }))
                  }
                />
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Parent Category
                  </label>
                  <select
                    value={formData.parent_id || ''}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        parent_id: e.target.value || null,
                      }))
                    }
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="">No parent</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.translations.find((t) => t.locale === 'en')
                          ?.name || category.slug}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      is_active: e.target.checked,
                    }))
                  }
                  className="mr-2"
                />
                <label htmlFor="is_active">Active</label>
              </div>

              {languages.map((language) => {
                const isCurrentLocale = language.code === currentLanguageCode
                const isDisabled =
                  editingCategory && !hasTranslation && isCurrentLocale
                return (
                  <div key={language.id} className="border rounded-lg p-4">
                    <h3 className="font-semibold mb-2">{language.name}</h3>
                    <div className="space-y-2">
                      <Input
                        label="Name"
                        value={formData.translations[language.code]?.name || ''}
                        onChange={(e) =>
                          updateTranslation(
                            language.code,
                            'name',
                            e.target.value
                          )
                        }
                        required
                        disabled={isDisabled}
                      />
                      <Input
                        label="Description"
                        value={
                          formData.translations[language.code]?.description ||
                          ''
                        }
                        onChange={(e) =>
                          updateTranslation(
                            language.code,
                            'description',
                            e.target.value
                          )
                        }
                        disabled={isDisabled}
                      />
                      <Input
                        label="Meta Description"
                        value={
                          formData.translations[language.code]
                            ?.meta_description || ''
                        }
                        onChange={(e) =>
                          updateTranslation(
                            language.code,
                            'meta_description',
                            e.target.value
                          )
                        }
                        disabled={isDisabled}
                      />
                    </div>
                  </div>
                )
              })}

              <div className="flex gap-2">
                <Button
                  type="submit"
                  disabled={
                    createMutation.isPending || updateMutation.isPending
                  }
                >
                  {editingCategory ? 'Update' : 'Create'}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setIsCreating(false)
                    setEditingCategory(null)
                    resetForm()
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Sort Order
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {categories.map((category) => {
              const isMissingInCurrentLocale =
                !category.has_translation_for_locale
              return (
                <tr
                  key={category.id}
                  className={
                    isMissingInCurrentLocale ? 'opacity-50 bg-gray-50' : ''
                  }
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div>
                        <div className="flex items-center gap-2">
                          <div
                            className={`text-sm font-medium ${
                              isMissingInCurrentLocale
                                ? 'text-gray-500'
                                : 'text-gray-900'
                            }`}
                          >
                            {category.translations.find(
                              (t) => t.locale === currentLanguageCode
                            )?.name ||
                              category.translations.find(
                                (t) => t.locale === 'en'
                              )?.name ||
                              category.slug}
                          </div>
                          {isMissingInCurrentLocale && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700">
                              Missing Translation
                            </span>
                          )}
                        </div>
                        <div
                          className={`text-sm ${
                            isMissingInCurrentLocale
                              ? 'text-gray-400'
                              : 'text-gray-500'
                          }`}
                        >
                          {category.slug}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {category.type}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        category.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {category.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {category.sort_order}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => handleEdit(category)}
                        className="text-gray-400 hover:text-gray-600"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(category.id)}
                        className="text-red-400 hover:text-red-600"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default Categories
